import { PMSmallStatus } from "@pimo/pimo-components/src/lib/pm-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";

import { getStreetlightStatusValue } from "../../../utils/dropdown-options";
import { isFieldEnabled } from "../../field-enabled";
import { DashboardCardContext } from "../context";
import { statusReportingLevel } from "../status-reporting-level";

/**
 * The overall status card, and — when the program reports status per category
 * — the accumulated one.
 *
 * These used to be two `addComponent` calls that both registered
 * `componentId: "statusComponent"`, each with a `mapVisibility` that showed it
 * for one value of `statusReportingLevel` and hid it for the other. Two
 * components under one id is not something the drag-and-drop layout can tell
 * apart: the id is the key it saves a slot under and the key
 * `dashboardComponentValues` is stored against.
 *
 * They are one component here instead, choosing its shape from the same
 * setting the two `mapVisibility` calls used to. Giving the second one its own
 * id was the other option and is the worse one — the id is already written
 * into `AUDIT_VIEW_CONFIG` and friends and into every layout a customer has
 * saved, so a new id would simply not be placed, and programs on
 * `statusReportingLevel: "category"` would lose the card.
 */
export function addStatusCard(ctx: DashboardCardContext) {
  const { view, getCardCopy } = ctx;

  const statusComponent = view.addComponent({
    component: PMSmallStatus,
    layoutProps: {
      componentId: "statusComponent",
      title: "",
      description: "",
      labels: ["project-status"],
    },
  });

  statusComponent.mapLayoutProps(({ language, settings }) =>
    statusReportingLevel(settings) === "category"
      ? {
          componentId: "statusComponent",
          labels: ["#ofStatuses"],
          description: getLocalizedMessage(language || "en", "showsNumberOfX", {
            x: getLocalizedMessage(language, "statuses"),
          }),
          title: getLocalizedMessage(language, "status"),
        }
      : {
          componentId: "statusComponent",
          labels: ["project-status"],
          title: getCardCopy(language, "overallStatus").title,
          description: getCardCopy(language, "overallStatus").description,
        }
  );

  statusComponent.mapState(({ dashboard, language, settings }) => {
    if (statusReportingLevel(settings) === "category") {
      const overallSemaphoreStatus =
        dashboard?._calculated?.overallSemaphoreStatus;
      const semaphoreStatus1 = dashboard?._calculated?.semaphoreStatus1;
      const semaphoreStatus2 = dashboard?._calculated?.semaphoreStatus2;
      const semaphoreStatus3 = dashboard?._calculated?.semaphoreStatus3;
      return {
        title: getLocalizedMessage(language, "status") || "Status",
        primaryStatusIndicatorSize: "large",
        fixedHeight: "134px",
        subStatusesDisplay: "column",
        status: getStreetlightStatusValue(overallSemaphoreStatus) ?? "",
        subStatuses: [semaphoreStatus1, semaphoreStatus2, semaphoreStatus3].map(
          (status, index) => ({
            name:
              settings?.configuration?.semaphoreStatusDefinition?.[index]
                ?.name ?? "",
            status: getStreetlightStatusValue(status) ?? "",
          })
        ),
      };
    }

    const overallStatus = dashboard?._calculated?.overallStatus;

    return {
      title: getLocalizedMessage(language, "status") || "Status",
      primaryStatusIndicatorSize: "large",
      fixedHeight: "134px",
      status: getStreetlightStatusValue(overallStatus) ?? "",
    };
  });

  statusComponent.mapVisibility(({ settings }) =>
    isFieldEnabled(settings, "status", "itemReporting")
  );
}
